import React, { useState, useMemo } from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Box,
  Switch,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  Close as CancelIcon,
  ContentCopy as DuplicateIcon,
  ShowChart as ShowChartIcon,
  Check as SaveIcon,
} from '@mui/icons-material';
import { Ad, AdStatus } from '../../../types/ads.types';
import { formatCurrency, formatPercentage, formatNumber, formatDate } from '../../../utils/formatters';
import {
  calculateCoverage,
  calculatePageCTRFromBreakdown,
  calculateRPM,
  calculateRPC,
  calculateCPA,
  calculateCPC,
} from '../../../utils/adsMetrics';
import TrendChart from '../../../components/TrendChart/TrendChart';
import { DayBreakdownChart } from './DayBreakdownChart';

interface AdRowProps {
  ad: Ad;
  selected: boolean;
  showGraphs: boolean;
  breakdownMode: 'daily' | 'hourly';
  updatingAdsetId?: string | null;
  updatingAdId?: string | null;
  onSelect: () => void;
  onUpdateStatus: (adId: string, status: AdStatus) => void;
  onUpdateBudget: (adsetId: string, budget: number) => void;
  onDuplicate?: (adId: string) => void;
  expandedAds?: Set<string>;
  onToggleAdExpansion?: (adId: string) => void;
  expandedDates?: Set<string>;
  onToggleDateExpansion?: (adId: string, date: string) => void;
}

/**
 * AdRow - Displays a single ad row with all metrics
 *
 * Features:
 * - Checkbox for selection
 * - Status toggle (ACTIVE/PAUSED)
 * - Budget display/edit
 * - All performance metrics
 * - Trend chart (daily/hourly)
 * - Date information
 */
export const AdRow: React.FC<AdRowProps> = ({
  ad,
  selected,
  showGraphs,
  breakdownMode,
  updatingAdsetId,
  updatingAdId,
  onSelect,
  onUpdateStatus,
  onUpdateBudget,
  onDuplicate,
  expandedAds,
  onToggleAdExpansion,
  expandedDates: controlledExpandedDates,
  onToggleDateExpansion,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [budgetValue, setBudgetValue] = useState(ad.adsetDailyBudget?.toString() || '');
  const [internalExpandedDates, setInternalExpandedDates] = useState<Set<string>>(new Set());

  // State to track which individual days have their graphs showing
  const [dayGraphsExpanded, setDayGraphsExpanded] = useState<Set<string>>(new Set());

  // Check if this ad is currently being updated (status or budget)
  const isUpdatingBudget = updatingAdsetId === ad.adsetId;
  const isUpdatingStatus = updatingAdId === ad.adId;
  const isUpdating = isUpdatingBudget || isUpdatingStatus;

  // Toggle graph for a specific day
  const toggleDayGraph = (date: string) => {
    setDayGraphsExpanded(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  // Use controlled or internal state for ad expansion
  const expanded = expandedAds ? expandedAds.has(ad.adId) : internalExpanded;
  const toggleExpanded = () => {
    if (onToggleAdExpansion) {
      onToggleAdExpansion(ad.adId);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  // Use controlled or internal state for date expansion
  const expandedDates = controlledExpandedDates ?? internalExpandedDates;
  const setExpandedDates = onToggleDateExpansion ? undefined : setInternalExpandedDates;

  // Update budget value when ad changes
  React.useEffect(() => {
    setBudgetValue(ad.adsetDailyBudget?.toString() || '');
  }, [ad.adsetDailyBudget]);

  // Group hourly data by date
  const hourlyByDate = useMemo(() => {
    if (!ad.hourlyBreakdown) return [];

    const grouped = new Map<string, typeof ad.hourlyBreakdown>();
    ad.hourlyBreakdown.forEach(hour => {
      if (!grouped.has(hour.date)) {
        grouped.set(hour.date, []);
      }
      grouped.get(hour.date)!.push(hour);
    });

    // Sort dates descending, hours ascending within each date
    return Array.from(grouped.entries())
      .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
      .map(([date, hours]) => ({
        date,
        hours: hours.sort((a, b) => a.hour - b.hour)
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad.hourlyBreakdown]);

  // Set first date as expanded by default (only for internal state)
  React.useEffect(() => {
    if (expanded && breakdownMode === 'hourly' && hourlyByDate.length > 0 && setExpandedDates) {
      setExpandedDates(new Set([`${ad.adId}-${hourlyByDate[0].date}`]));
    }
  }, [expanded, breakdownMode, hourlyByDate, ad.adId, setExpandedDates]);

  const toggleDate = (date: string) => {
    if (onToggleDateExpansion) {
      onToggleDateExpansion(ad.adId, date);
    } else if (setExpandedDates) {
      setExpandedDates(prev => {
        const next = new Set(prev);
        const key = `${ad.adId}-${date}`;
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    }
  };

  // Check if ad has breakdown data
  const hasBreakdownData = breakdownMode === 'daily'
    ? (ad.dailyBreakdown && ad.dailyBreakdown.length > 0)
    : (ad.hourlyBreakdown && ad.hourlyBreakdown.length > 0);

  // Handle duplicate
  const handleDuplicate = () => {
    if (onDuplicate) {
      onDuplicate(ad.adId);
    }
  };

  // Calculate days running
  const daysRunning = React.useMemo(() => {
    if (!ad.adCreatedTime) return null;
    const createdDate = new Date(ad.adCreatedTime);
    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return days;
  }, [ad.adCreatedTime]);

  // Copy to clipboard
  const handleCopy = (text: string, _label: string) => {
    navigator.clipboard.writeText(text);
    // Note: Consider adding a toast notification for better UX
  };

  // Handle status toggle
  const handleStatusToggle = () => {
    const newStatus: AdStatus = ad.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    onUpdateStatus(ad.adId, newStatus);
  };

  // Prepare trend chart data
  const trendData = React.useMemo(() => {
    if (breakdownMode === 'daily' && ad.dailyBreakdown) {
      return ad.dailyBreakdown.map((d) => ({
        date: d.date,
        value: d.roas,
        label: `${formatDate(d.date)}: ${formatPercentage(d.roas)}`,
      }));
    } else if (breakdownMode === 'hourly' && ad.hourlyBreakdown) {
      return ad.hourlyBreakdown.map((h) => ({
        date: `${h.date}-${h.hour}`,
        value: h.roas,
        label: `Hour ${h.hour}: ${formatPercentage(h.roas)}`,
      }));
    }
    return [];
  }, [ad, breakdownMode]);

  return (
    <>
      <TableRow
        selected={selected}
        hover
        sx={(theme) => ({
          bgcolor: selected ? '#eff6ff' : 'background.paper',
          '&:hover': {
            bgcolor: selected ? '#eff6ff' : '#f9fafb',
          },
          // Animated shimmer effect across entire row when updating
          ...(isUpdating && {
            position: 'relative',
            backgroundImage: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main}40 50%, transparent 100%)`,
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s ease-in-out infinite',
            '@keyframes shimmer': {
              '0%': {
                backgroundPosition: '200% 0',
              },
              '100%': {
                backgroundPosition: '-200% 0',
              },
            },
          }),
        })}
      >
        {/* Checkbox with Expand Button */}
        <TableCell padding="checkbox">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.5 }}>
            {hasBreakdownData && (
              <IconButton
                size="small"
                onClick={toggleExpanded}
                sx={{ p: 0.25 }}
              >
                {expanded ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
              </IconButton>
            )}
            {!hasBreakdownData && <Box sx={{ width: 28 }} />}
            <Checkbox checked={selected} onChange={onSelect} size="small" />
          </Box>
        </TableCell>

      {/* Status Toggle with Indicator and Duplicate Button */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Tooltip title={`Status: ${ad.status}`} arrow>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: ad.status === 'ACTIVE'
                  ? '#10b981'
                  : ad.status === 'PAUSED'
                  ? '#f59e0b'
                  : ad.status === 'ARCHIVED'
                  ? '#6b7280'
                  : '#ef4444',
                border: '2px solid',
                borderColor: ad.status === 'ACTIVE'
                  ? '#059669'
                  : ad.status === 'PAUSED'
                  ? '#d97706'
                  : ad.status === 'ARCHIVED'
                  ? '#4b5563'
                  : '#dc2626',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.8)',
                flexShrink: 0,
              }}
            />
          </Tooltip>
          <Switch
            checked={ad.status === 'ACTIVE'}
            onChange={handleStatusToggle}
            size="small"
            disabled={(ad.status !== 'ACTIVE' && ad.status !== 'PAUSED') || isUpdatingStatus}
          />
          <Tooltip title="Duplicate ad" arrow>
            <IconButton
              size="small"
              onClick={handleDuplicate}
              sx={{
                p: 0.5,
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <DuplicateIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>

      {/* Budget - Always Input with Save Button */}
      <TableCell>
        {ad.adsetDailyBudget ? (
          <Box sx={{ position: 'relative', width: 90 }}>
            <TextField
              value={budgetValue}
              onChange={(e) => setBudgetValue(e.target.value)}
              onBlur={() => {
                // Revert if invalid or empty on blur
                if (!budgetValue || parseFloat(budgetValue) <= 0) {
                  setBudgetValue(ad.adsetDailyBudget?.toString() || '');
                }
              }}
              disabled={isUpdating}
              size="small"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  fontSize: '0.813rem',
                  pr: (parseFloat(budgetValue) !== ad.adsetDailyBudget && budgetValue && parseFloat(budgetValue) > 0) || isUpdating ? 3.5 : 1,
                  transition: 'padding-right 0.2s',
                },
                '& .MuiInputBase-input': {
                  py: 0.5,
                  px: 1,
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: isUpdating
                      ? '#3b82f6'  // Blue border when loading
                      : parseFloat(budgetValue) !== ad.adsetDailyBudget && budgetValue && parseFloat(budgetValue) > 0
                        ? '#f59e0b'  // Orange border when changed
                        : 'rgba(0, 0, 0, 0.23)',  // Default border
                  },
                },
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const newBudget = parseFloat(budgetValue);
                  if (!isNaN(newBudget) && newBudget > 0 && newBudget !== ad.adsetDailyBudget && !isUpdating) {
                    onUpdateBudget(ad.adsetId, newBudget);
                    (e.target as HTMLInputElement).blur();
                  }
                }
                if (e.key === 'Escape') {
                  setBudgetValue(ad.adsetDailyBudget?.toString() || '');
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            {/* Save button - absolutely positioned (no spinner needed, we have shimmer animation) */}
            {!isUpdating && parseFloat(budgetValue) !== ad.adsetDailyBudget && budgetValue && parseFloat(budgetValue) > 0 && (
              <IconButton
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => {
                  const newBudget = parseFloat(budgetValue);
                  if (!isNaN(newBudget) && newBudget > 0) {
                    onUpdateBudget(ad.adsetId, newBudget);
                  }
                }}
                sx={{
                  position: 'absolute',
                  right: 3,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  p: 0.25,
                  minWidth: 'auto',
                  width: 20,
                  height: 20,
                  bgcolor: '#10b981',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#059669',
                  },
                  zIndex: 1,
                }}
              >
                <SaveIcon sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.813rem', color: 'text.secondary' }}>
            -
          </Typography>
        )}
      </TableCell>

      {/* Ad Name with Creative Image */}
      <TableCell sx={{ width: 350, maxWidth: 500 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Creative Thumbnail */}
          {ad.facebookAd?.creative?.thumbnail_url && (
            <Box
              component="img"
              src={ad.facebookAd.creative.thumbnail_url}
              alt={ad.adName}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                objectFit: 'cover',
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider',
              }}
              onError={(e) => {
                // Hide image on error
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* Ad Title with Days Running */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography
                variant="body2"
                onClick={() => hasBreakdownData && toggleExpanded()}
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.4,
                  cursor: hasBreakdownData ? 'pointer' : 'default',
                  '&:hover': hasBreakdownData ? {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  } : {},
                }}
              >
                {ad.adName}
              </Typography>
              {daysRunning !== null && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  ({daysRunning}d)
                </Typography>
              )}
            </Box>

            {/* AdSet and Ad IDs with Copy */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
              {/* AdSet ID */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  AdSet:
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                  {ad.adsetId}
                </Typography>
                <Tooltip title="Copy AdSet ID" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(ad.adsetId, 'AdSet ID')}
                    sx={{
                      p: 0.25,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <DuplicateIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                •
              </Typography>

              {/* Ad ID */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  Ad:
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                  {ad.adId}
                </Typography>
                <Tooltip title="Copy Ad ID" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(ad.adId, 'Ad ID')}
                    sx={{
                      p: 0.25,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <DuplicateIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
      </TableCell>

      {/* Advertiser */}
      <TableCell>
        <Typography variant="body2">{ad.advertiserName}</Typography>
      </TableCell>

      {/* Metrics */}
      <TableCell align="right">
        <Typography variant="body2">{formatCurrency(ad.spend)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatCurrency(ad.revenue)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{
            color: ad.pnl >= 0 ? 'success.main' : 'error.main',
            fontWeight: 500,
          }}
        >
          {formatCurrency(ad.pnl)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{
            color: ad.roas >= 1.0 ? 'success.main' : 'error.main',
            fontWeight: 500,
          }}
        >
          {formatPercentage(ad.roas)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatCurrency(ad.rpm)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatCurrency(ad.rpc)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatCurrency(ad.cpa)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatCurrency(ad.cpc)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatNumber(ad.clicks)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatNumber(ad.searches)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">
          {formatPercentage(calculatePageCTRFromBreakdown(ad.dailyBreakdown) / 100)}
        </Typography>
      </TableCell>

      {/* Dates */}
      <TableCell align="center" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>
        <Typography variant="caption">{formatDate(ad.adCreatedTime)}</Typography>
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>
        <Typography variant="caption">{formatDate(ad.adUpdatedTime)}</Typography>
      </TableCell>

      {/* Trend Chart */}
      {showGraphs && (
        <TableCell align="center">
          {trendData.length > 0 ? (
            <TrendChart
              data={trendData}
              height={32}
              barWidth={4}
              barGap={1}
              colorThresholds={[
                { value: 1.25, color: '#10b981' },
                { value: 1.05, color: '#22c55e' },
                { value: 0.8, color: '#f59e0b' },
                { value: 0, color: '#ef4444' },
              ]}
              valueFormatter={(v) => formatPercentage(v)}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>
      )}

      {/* Coverage */}
      <TableCell align="right">
        <Typography variant="body2">
          {formatPercentage(calculateCoverage(ad.dailyBreakdown) / 100)}
        </Typography>
      </TableCell>
      </TableRow>

      {/* Expanded Breakdown Rows - Daily */}
      {expanded && breakdownMode === 'daily' && ad.dailyBreakdown?.map((day, idx) => {
        // Get hourly data for this specific day
        const hoursForDay = (ad.hourlyBreakdown || [])
          .filter(h => h.date === day.date)
          .sort((a, b) => a.hour - b.hour);

        return (
          <TableRow
            key={`breakdown-${ad.adId}-${day.date}-${idx}`}
            sx={{
              bgcolor: '#f0f9ff',
              borderTop: '2px solid #cbd5e1',
              '&:hover': {
                bgcolor: '#e0f2fe',
              }
            }}
          >
            {/* Chart cell - spans first 4 columns (checkbox, status, budget, campaign) */}
            {showGraphs || dayGraphsExpanded.has(day.date) ? (
              <TableCell
                colSpan={4}
                sx={{
                  px: 0,
                  py: 1,
                  bgcolor: '#f8fafc',
                  verticalAlign: 'middle',
                  borderRight: '3px solid #e2e8f0',
                  position: 'relative'
                }}
              >
                {hoursForDay.length > 0 ? (
                  <Box sx={{ position: 'relative' }}>
                    <DayBreakdownChart hours={hoursForDay} height={280} />
                    {/* Close button - only show if individually expanded (not when showGraphs is globally on) */}
                    {!showGraphs && dayGraphsExpanded.has(day.date) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDayGraph(day.date);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          padding: '6px',
                          color: 'text.secondary',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            borderColor: 'primary.main',
                            color: 'white',
                          },
                          zIndex: 10,
                        }}
                      >
                        <CancelIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 280,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontStyle: 'italic'
                    }}
                  >
                    No hourly data available
                  </Box>
                )}
              </TableCell>
            ) : (
              <TableCell
                colSpan={4}
                onClick={() => toggleDayGraph(day.date)}
                sx={{
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  bgcolor: '#f1f5f9',
                  borderRight: '3px solid #e2e8f0',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#e2e8f0',
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 60,
                    color: '#475569',
                    fontSize: '0.75rem',
                    gap: 0.5,
                  }}
                >
                  <ShowChartIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                    Click to show graph
                  </Typography>
                </Box>
              </TableCell>
            )}

            {/* Advertiser column - show date */}
            <TableCell sx={{
              fontSize: '0.8rem',
              color: 'primary.main',
              fontWeight: 700,
              py: 1,
              bgcolor: 'white'
            }}>
              {formatDate(day.date)}
            </TableCell>

            {/* Metrics - same columns as main row */}
            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, bgcolor: 'white' }}>
              {formatCurrency(day.spend)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, bgcolor: 'white' }}>
              {formatCurrency(day.revenue)}
            </TableCell>
            <TableCell align="right" sx={{ py: 1, bgcolor: 'white' }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: (day.revenue - day.spend) >= 0 ? 'success.main' : 'error.main'
                }}
              >
                {formatCurrency(day.revenue - day.spend)}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ py: 1, bgcolor: 'white' }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: day.roas >= 1.0 ? 'success.main' : 'error.main',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: day.roas >= 1.0 ? '#f0fdf4' : '#fef2f2',
                  display: 'inline-block'
                }}
              >
                {formatPercentage(day.roas)}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', py: 1, bgcolor: 'white' }}>
              {formatCurrency(day.rpm)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', py: 1, bgcolor: 'white' }}>
              {formatCurrency(day.rpc)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', py: 1, bgcolor: 'white' }}>
              {formatCurrency(day.cpa)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', py: 1, bgcolor: 'white' }}>
              {formatCurrency(day.cpc)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500, py: 1, bgcolor: 'white' }}>
              {formatNumber(day.clicks)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500, py: 1, bgcolor: 'white' }}>
              {formatNumber(day.searches)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem', py: 1, bgcolor: 'white' }}>
              {formatPercentage(day.pageCtr / 100)}
            </TableCell>

            {/* Date columns */}
            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1, color: 'text.secondary', bgcolor: 'white', minWidth: 110, whiteSpace: 'nowrap' }}>
              {formatDate(ad.adCreatedTime)}
            </TableCell>
            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1, color: 'text.secondary', bgcolor: 'white', minWidth: 110, whiteSpace: 'nowrap' }}>
              {formatDate(ad.adUpdatedTime)}
            </TableCell>

            {/* Trend - show mini chart */}
            {showGraphs && (
              <TableCell align="center" sx={{ py: 1, bgcolor: 'white' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: day.roas >= 1.0 ? '#10b981' : '#ef4444',
                    mx: 'auto',
                    boxShadow: `0 0 0 2px ${day.roas >= 1.0 ? '#f0fdf4' : '#fef2f2'}`
                  }}
                />
              </TableCell>
            )}

            {/* Coverage - empty for daily rows */}
            <TableCell align="right" sx={{ fontSize: '0.8rem', py: 1, bgcolor: 'white' }}>
            </TableCell>
          </TableRow>
        );
      })}

      {/* Expanded Breakdown - Hourly Rows Grouped by Date */}
      {expanded && breakdownMode === 'hourly' && hourlyByDate.map(({ date, hours }) => {
        const isDateExpanded = expandedDates.has(`${ad.adId}-${date}`);
        const dateObj = new Date(date);
        const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        return (
          <React.Fragment key={`date-group-${ad.adId}-${date}`}>
            {/* Date Header Row */}
            <TableRow
              onClick={() => toggleDate(date)}
              sx={{
                bgcolor: '#f9fafb',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#f3f4f6',
                }
              }}
            >
              <TableCell colSpan={4} sx={{ py: 1, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 6 }}>
                  <IconButton size="small" sx={{ p: 0 }}>
                    {isDateExpanded ? <ExpandIcon /> : <CollapseIcon />}
                  </IconButton>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                    {formattedDate}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell colSpan={16} sx={{ py: 1 }} />
            </TableRow>

            {/* Hourly Rows for this Date */}
            {isDateExpanded && hours.map((hour, idx) => {
              const isFirstRow = idx === 0;
              // Calculate metrics using utility functions
              const rpm = calculateRPM(hour.revenue, hour.searches);
              const rpc = calculateRPC(hour.revenue, hour.clicks);
              const cpa = calculateCPA(hour.spend, hour.searches);
              const cpc = calculateCPC(hour.spend, hour.clicks);
              const pageCtr = hour.searches > 0 ? (hour.clicks / hour.searches) * 100 : 0;

              return (
                <TableRow
                  key={`breakdown-hourly-${ad.adId}-${date}-${hour.hour}-${idx}`}
                  sx={{
                    bgcolor: '#f0f9ff',
                    ...(isFirstRow && { borderTop: '2px solid #cbd5e1' }),
                    '&:hover': {
                      bgcolor: '#e0f2fe',
                    }
                  }}
                >
                  {/* Chart cell - only on first row, spans all hourly rows for this date */}
                  {isFirstRow && (showGraphs || dayGraphsExpanded.has(date)) && (
                    <TableCell
                      rowSpan={hours.length}
                      colSpan={4}
                      sx={{
                        px: 0,
                        py: 1,
                        bgcolor: '#f8fafc',
                        verticalAlign: 'top',
                        borderRight: '3px solid #e2e8f0',
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <DayBreakdownChart
                          hours={hours.sort((a, b) => a.hour - b.hour)}
                          height={350}
                        />
                        {/* Close button - only show if individually expanded (not when showGraphs is globally on) */}
                        {!showGraphs && dayGraphsExpanded.has(date) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDayGraph(date);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(255, 255, 255, 0.95)',
                              border: '1px solid #e2e8f0',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              padding: '6px',
                              color: 'text.secondary',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                borderColor: 'primary.main',
                                color: 'white',
                              },
                              zIndex: 10,
                            }}
                          >
                            <CancelIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  )}

                  {/* Clickable area when graphs are hidden - only on first row */}
                  {isFirstRow && !showGraphs && !dayGraphsExpanded.has(date) && (
                    <TableCell
                      rowSpan={hours.length}
                      colSpan={4}
                      onClick={() => toggleDayGraph(date)}
                      sx={{
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        bgcolor: '#f1f5f9',
                        borderRight: '3px solid #e2e8f0',
                        verticalAlign: 'middle',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#e2e8f0',
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          color: '#475569',
                          fontSize: '0.75rem',
                          gap: 0.5,
                          flexDirection: 'column',
                        }}
                      >
                        <ShowChartIcon sx={{ fontSize: 20 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                          Click to show graph
                        </Typography>
                      </Box>
                    </TableCell>
                  )}

                  {/* Advertiser column - show hour */}
                  <TableCell sx={{
                    fontSize: '0.8rem',
                    color: 'text.primary',
                    fontWeight: 600,
                    py: 0.75,
                    pl: 2,
                    bgcolor: 'white'
                  }}>
                    {hour.hour}:00-{hour.hour + 1}:00
                  </TableCell>

                  {/* Metrics */}
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 0.75, bgcolor: 'white' }}>
                    {formatCurrency(hour.spend)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 0.75, bgcolor: 'white' }}>
                    {formatCurrency(hour.revenue)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.75, bgcolor: 'white' }}>
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: (hour.revenue - hour.spend) >= 0 ? 'success.main' : 'error.main'
                      }}
                    >
                      {formatCurrency(hour.revenue - hour.spend)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.75, bgcolor: 'white' }}>
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: hour.roas >= 1.0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {formatPercentage(hour.roas)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.75, bgcolor: 'white' }}>
                    {formatCurrency(rpm)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.75, bgcolor: 'white' }}>
                    {formatCurrency(rpc)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.75, bgcolor: 'white' }}>
                    {formatCurrency(cpa)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.75, bgcolor: 'white' }}>
                    {formatCurrency(cpc)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500, py: 0.75, bgcolor: 'white' }}>
                    {formatNumber(hour.clicks)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500, py: 0.75, bgcolor: 'white' }}>
                    {formatNumber(hour.searches)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.75, bgcolor: 'white' }}>
                    {formatPercentage(pageCtr / 100)}
                  </TableCell>

                  {/* Date columns - empty */}
                  <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.75, color: 'text.secondary', bgcolor: 'white', minWidth: 110 }}>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.75, color: 'text.secondary', bgcolor: 'white', minWidth: 110 }}>
                  </TableCell>

                  {/* Trend - show mini indicator */}
                  {showGraphs && (
                    <TableCell align="center" sx={{ py: 0.75, bgcolor: 'white' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: hour.roas >= 1.0 ? '#10b981' : '#ef4444',
                          mx: 'auto',
                          boxShadow: `0 0 0 2px ${hour.roas >= 1.0 ? '#f0fdf4' : '#fef2f2'}`
                        }}
                      />
                    </TableCell>
                  )}

                  {/* Coverage - empty */}
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.75, bgcolor: 'white' }}>
                  </TableCell>
                </TableRow>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default AdRow;
